import { useCallback } from 'react';

import { getUploadUrl, scanFile } from '../reducers/attachment';
import { uploadFileWithProgress } from '../services/attachments';
import useAppDispatch from './useAppDispatch';

export interface UploadedFile {
    id: number;
    file: File;
    fileId?: number;
    processed?: boolean;
    uploadProgress?: number;
    status?: 'uploading' | 'completed' | 'error';
    errorMessage?: string;
    uploadController?: AbortController;
}

interface UseFileUploadWithValidationOptions {
    workId?: number;
    isBug?: boolean;
    onFilesUpdate: (updater: (prevFiles: UploadedFile[]) => UploadedFile[]) => void;
    onError: (error: string) => void;
    onFileComplete?: (fileId: number) => void;
    onFileRemove?: (fileId: number) => void;
}

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes

const useFileUploadWithValidation = ({
    workId,
    isBug = false,
    onFilesUpdate,
    onError,
    onFileComplete,
    onFileRemove
}: UseFileUploadWithValidationOptions): (filesWithIds: { file: File; id: number }[]) => Promise<void> => {
    const dispatch = useAppDispatch();

    const handleFilesSelect = useCallback(async (
        filesWithIds: { file: File; id: number }[]
    ): Promise<void> => {
        const validFiles: { file: File; id: number }[] = [];
        const invalidFiles: string[] = [];

        filesWithIds.forEach(({ file, id }) => {
            if (file.size > MAX_FILE_SIZE) {
                invalidFiles.push(file.name);
            } else {
                validFiles.push({ file, id });
            }
        });

        if (invalidFiles.length > 0) {
            const errorMessage = `The following files exceed the 20MB size limit and have been removed: ${invalidFiles.join(', ')}`;
            onError(errorMessage);
        }
        if (validFiles.length === 0) {
            return;
        }

        const newAttachedFiles = validFiles.map(({ file, id: fileId }) => ({
            id: fileId,
            file: file,
            uploadProgress: 0,
            status: 'uploading' as const
        }));

        onFilesUpdate((prevFiles) => [ ...prevFiles, ...newAttachedFiles ]);

        for (const attachedFile of newAttachedFiles) {
            let fileId: number;
            try {
                const response = await dispatch(
                    getUploadUrl({
                        fileName: attachedFile.file.name,
                        fileType: attachedFile.file.type,
                        workId,
                        isBug
                    })
                ).unwrap();

                const uploadUrl = response.url;
                fileId = response.fileId;

                const uploadController = new AbortController();
                const signal = uploadController.signal;

                onFilesUpdate((prevFiles) =>
                    prevFiles.map((f) =>
                        f.id === attachedFile.id
                            ? { ...f, fileId, uploadController }
                            : f
                    )
                );

                await uploadFileWithProgress({
                    uploadUrl,
                    file: attachedFile.file,
                    fileId: attachedFile.id,
                    signal,
                    onProgress: (fileId, progress) => {
                        onFilesUpdate((prevFiles) =>
                            prevFiles.map((f) =>
                                f.id === fileId
                                    ? { ...f, uploadProgress: progress }
                                    : f
                            )
                        );
                    },
                    onComplete: (completedFileId, success, error) => {
                        if (success) {
                            onFilesUpdate((prevFiles) =>
                                prevFiles.map((f) =>
                                    f.id === completedFileId
                                        ? { ...f, status: 'completed' as const }
                                        : f
                                )
                            );
                        } else {
                            console.error('Upload error:', error);
                            onFilesUpdate((prevFiles) =>
                                prevFiles.map((f) =>
                                    f.id === completedFileId
                                        ? { ...f, status: 'error' as const }
                                        : f
                                )
                            );
                        }
                    }
                });

                const scanResult = await dispatch(
                    scanFile({
                        fileId
                    })
                ).unwrap();

                if (!scanResult.isSafe) {
                    onFilesUpdate((prevFiles) =>
                        prevFiles.filter((f) => f.id !== attachedFile.id)
                    );
                    onFileRemove?.(fileId);
                } else {
                    onFilesUpdate((prevFiles) =>
                        prevFiles.map((f) =>
                            f.id === attachedFile.id
                                ? { ...f, status: 'completed' as const }
                                : f
                        )
                    );
                    onFileComplete?.(fileId);
                }
            } catch (error) {
                console.error('Upload error:', error);
                onError(`Failed to upload ${attachedFile.file.name}, please try again`);
                onFilesUpdate((prevFiles) =>
                    prevFiles.filter((f) => f.id !== attachedFile.id)
                );
                if (attachedFile.id) {
                    onFileRemove?.(attachedFile.id);
                }
            }
        }
    }, [ dispatch, workId, isBug, onFilesUpdate, onError, onFileComplete, onFileRemove ]);

    return handleFilesSelect;
};

export default useFileUploadWithValidation;

